// AnomalyPopover — bottom-sheet popover for inline ⚠ chips (CR2-10).
// Mobile-first: fixed bottom, max-width 430px. Backdrop click, ✕ button
// and Escape key all close. "Tam liste için /anomaliler →" link navigates
// and closes.
//
// Click safety: chip sits inside <button> (SinyalSatiri) and inside <table>
// row (AnnePayiTablosu). The popover is rendered via createPortal to
// document.body so the popover DOM lives outside the parent <button>;
// React's synthetic event system still bubbles through the component tree
// regardless, so every interactive surface here stops propagation too.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { fmtDateTime } from "../lib/format";
import type { Anomaly, AnomalyPayload } from "../types/api";

interface Props {
  fund: string;
  anomalies: Anomaly[];
  onClose: () => void;
}

function formatValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return JSON.stringify(v, null, 2);
}

function PayloadKV({ payload }: { payload: AnomalyPayload }) {
  const entries = Object.entries(payload);
  return (
    <dl className="fon-dili text-[11px] grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-ink-400">{k}</dt>
          <dd className="text-ink-200 break-words whitespace-pre-wrap">
            {formatValue(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function AnomalyPopover({ fund, anomalies, onClose }: Props) {
  // Escape to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  function handleBackdrop(e: React.MouseEvent) {
    e.stopPropagation();
    onClose();
  }

  function handleSheet(e: React.MouseEvent) {
    e.stopPropagation();
  }

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-950/75 backdrop-blur-sm anomaly-backdrop"
        onClick={handleBackdrop}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${fund} için açık veri uyarıları`}
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center pointer-events-none"
        onClick={handleSheet}
      >
        <div
          className="w-full max-w-[430px] pointer-events-auto anomaly-sheet bg-ink-800 border border-ink-700 border-b-0 rounded-t-2xl shadow-2xl"
          onClick={handleSheet}
        >
          <div className="flex justify-center pt-2 pb-1">
            <span aria-hidden="true" className="block w-10 h-1 rounded-full bg-ink-600" />
          </div>

          <header className="flex items-start justify-between gap-2 px-4 pt-1 pb-2">
            <div className="min-w-0">
              <h2 className="fon-dili text-[13px] text-ink-100 font-semibold">
                {fund}
              </h2>
              <p className="text-[10px] uppercase tracking-wider text-warn-400 mt-0.5">
                {anomalies.length} açık veri uyarısı
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Kapat"
              className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-ink-300 hover:text-ink-100 hover:bg-ink-700 transition"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="px-4 pb-3 max-h-[55vh] overflow-y-auto no-scrollbar">
            <ul className="space-y-3">
              {anomalies.map((a) => (
                <li
                  key={a.id}
                  className="rounded-xl bg-ink-900/70 border border-ink-700/70 p-3"
                >
                  <p className="hedef-dili text-ink-100 text-[13px] leading-snug">
                    {a.message}
                  </p>
                  {a.impact && (
                    <p className="hedef-dili text-ink-300 text-[11px] mt-1.5 leading-snug">
                      {a.impact}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-ink-400 fon-dili">
                    <span>{a.fund}</span>
                    <span aria-hidden="true">·</span>
                    <span>{fmtDateTime(a.triggered_at)}</span>
                  </div>
                  <details className="mt-2">
                    <summary className="text-[11px] text-ink-300 cursor-pointer select-none hover:text-ink-100 transition">
                      <span className="caret inline-block mr-1">▸</span>
                      Payload
                    </summary>
                    <div className="mt-2 p-2 rounded-lg bg-ink-950/60 border border-ink-700/50">
                      <PayloadKV payload={a.payload} />
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>

          <footer className="px-4 py-3 border-t border-ink-700/60">
            <Link
              to="/anomaliler"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="block text-center text-[12px] font-medium text-accent-300 hover:text-accent-400 transition"
            >
              Tam liste için /anomaliler →
            </Link>
          </footer>
        </div>
      </div>
    </>,
    document.body
  );
}
